import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
// Import Shared Components
import { Loader, MFAVerifyForm } from 'shared-components';
import { MFAStorage, MFAErrors, verifyMFA as genericVerifyMFA } from 'shared-components/src/utils/mfa';
// Import Local Components
import AuthLayout from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
// Import Utils
import { destinationAfterAuth } from '../../../utils/pinSetup';

/**
 * Refactored MFAVerify component using reusable MFA components
 * This demonstrates how any portal can use the shared MFA utilities and components
 */
function MFAVerify() {
  const intl = useIntl();
  const navigate = useNavigate();

  // Set States
  const [formError, setFormError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [userSub, setUserSub] = useState(null);

  useEffect(() => {
    // Get user sub from session storage (set during login)
    const storedUserSub = MFAStorage.getUserSub();

    if (!storedUserSub) {
      // No user sub available, redirect to login
      navigate('/login', {
        state: {
          error: 'mfa-data-not-available',
          errorDetails: 'MFA verification data not found'
        }
      });
      return;
    }

    setUserSub(storedUserSub);
    setLoading(false);
  }, [navigate]);

  const handleMFAVerificationComplete = async (userSub, totpCode) => {
    try {
      setVerifying(true);
      setFormError(null);

      // Use the generic MFA verify utility with cardholder-specific endpoint
      await genericVerifyMFA(
        '/api/v1/cardholder_portal/mfa/verify',
        userSub,
        totpCode,
        // onSuccess callback
        () => {
          // Clear MFA session data
          MFAStorage.clearAll();
          // Registration offers a PIN step after every auth factor is done.
          // Reached from registration when MFA was already registered, so the
          // marker has to be consumed here too, not just on MFA setup.
          window.location.pathname = destinationAfterAuth();
        },
        // onError callback
        (error) => {
          setVerifying(false);

          // Get the user-friendly error message
          const userError = MFAErrors.getUserFriendlyError(error);

          // Check if it's an authentication error or validation error
          if (MFAErrors.isAuthError(error)) {
            // Redirect to login for authentication errors
            navigate('/login', {
              state: {
                error: error.message,
                errorDetails: error.details
              }
            });
          } else if (userError === 'errors-mfa-not-setup') {
            // MFA is not set up - redirect to setup page with helpful message
            navigate('/login', {
              state: {
                error: 'mfa-not-setup',
                errorDetails: 'Please complete MFA setup first'
              }
            });
          } else {
            // Keep on page for validation errors (wrong TOTP code, etc.)
            setFormError(userError);
          }
        }
      );
    } catch (error) {
      // Error already handled in onError callback
      console.error('Unexpected error in MFA verification:', error);
    }
  };


  if (loading) {
    return (
      <AuthLayout
        page="mfa-verify"
        heading={intl.messages['mfa-verify-heading']}
        subHeaderOne={intl.messages['mfa-verify-loading']}
      >
        <Loader />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      page="mfa-verify"
      heading={intl.messages['mfa-verify-heading']}
      subHeaderOne={intl.messages['mfa-verify-subheader1-text']}
      subHeaderTwo={intl.messages['mfa-verify-subheader2-text']}
      error={formError ? intl.messages[formError] || formError : null}
    >
      <AuthForm
        page="mfa-verify"
        loading={verifying}
        primaryButtonText={intl.messages['mfa-verify-button']}
        secondaryButtonText={intl.messages['mfa-verify-back-button']}
        secondaryButtonFunc={() => {
          setFormError(null);
          navigate('/login');
        }}
      >
        <MFAVerifyForm
          userSub={userSub}
          onVerificationComplete={handleMFAVerificationComplete}
          loading={verifying}
          error={formError ? intl.messages[formError] || formError : null}
          disabled={verifying}
          autoFocus={!verifying}
          labels={{
            instruction: intl.messages['mfa-verify-subheader1-text'],
            otpLabel: intl.messages['mfa-totp-code-label'],
          }}
        />
      </AuthForm>
    </AuthLayout>
  );
};

export default MFAVerify;